import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Subscription, from, of } from 'rxjs';
import { delay, exhaustMap, filter, repeat, skipUntil } from 'rxjs/operators';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { environment } from 'src/environments/environment';
import * as AppActions from '../app/state/app/app.actions';
import { AuthService } from './auth/auth.service';
import { NotificationHandlerService } from './notifications/notification-handler.service';
import { AppMaintenanceModeDto, AppService, UserAppAccessStatus } from './services/api.services';
import { AppInitResult, AppInitService } from './services/app-init.service';
import { logger } from './pbir/utils/logger';

const MAINTENANCE_MODE_CHECK_INTERVAL = environment.pullRates.maintanceMode;

const LOGGER = logger('AppComponent');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent implements OnDestroy {
  appUnavailable = false;
  showDialog = false;

  maintenance = false;

  titleText = '';
  subtitleText = '';

  showButton = false;
  buttonText = 'Login';
  buttonAction = () => null;

  constructor(
    private ref: ChangeDetectorRef,
    private appInitService: AppInitService,
    private appService: AppService,
    private notificationService: NotificationHandlerService,
    private authService: AuthService,
    private store: Store,
    private router: Router) {
    if (isCanvasRendererHost) {
      // Get rid of the bg for embed host
      document.getElementById('app-bg')?.remove();

      this.appUnavailable = false;
      this.showDialog = false;
      this.maintenance = false;
      return;
    }

    this.appInitService.done$.subscribe(() => {
      if (this.appInitService.result === AppInitResult.Unauthenticated)
        return;

      if (this.appInitService.result === AppInitResult.Success && this.authService.loggedIn)
        this.notificationService.initNotificationHandler();

      this.initCheckAppState();
    });
  }

  private _checkAppStateSub: Subscription = null;

  private initCheckAppState() {
    this._checkAppStateSub =
      of(null).pipe(
        skipUntil(this.appInitService.done$),
        filter(() => MAINTENANCE_MODE_CHECK_INTERVAL != null),
        exhaustMap(() => from(this.checkAppState())),
        delay(MAINTENANCE_MODE_CHECK_INTERVAL),
        repeat(),
      ).subscribe();
  }

  private async checkAppState() {
    try {
      const maintenanceMode = await this.appService.getMaintenanceMode().toPromise();
      
      const userAccess = this.authService.loggedIn
        ? await this.getUserAccess()
        : UserAppAccessStatus.Unauthorized;

      let maintenance = false;
      const appUnavailable = userAccess !== UserAppAccessStatus.Accessible;

      if (maintenanceMode.active) {
        maintenance = true;

        if (userAccess !== UserAppAccessStatus.Accessible) {
          this.handleMaintenanceMode(maintenanceMode, userAccess);
        }
      }

      if (this.maintenance && !maintenance) {
        // Maintenance ended?
        window.location.reload();
        return;
      }

      this.maintenance = maintenance;
      this.appUnavailable = appUnavailable;
    }
    catch (err) {
      LOGGER.error('App state check error', err);
    }
    finally {
      this.ref.detectChanges();
    }
  }

  private async getUserAccess() {
    try {
      return await this.appService.getUserAccess().toPromise();
    }
    catch (err) {
      LOGGER.error(err);

      return UserAppAccessStatus.Unauthorized;
    }
  }

  private handleMaintenanceMode(maintenanceMode: AppMaintenanceModeDto, userAccess: UserAppAccessStatus) {
    this.titleText = 'Application Maintenance';

    this.maintenance = true;

    const subtitleText = [`Insights is in maintenance.`];

    const endDateMoment = moment.utc(maintenanceMode.endDateUtc);
    if (endDateMoment.isValid()) {
      const endDate = endDateMoment.local().format('L LT');
      subtitleText.push(`The maintenance is due to complete by ${endDate}.`);
    }

    if (userAccess === UserAppAccessStatus.InaccessibleMaintenance) {
      subtitleText.push(`You may still logout from the application.`);
      // If user is logged in, allow them to logout.
      this.showButton = true;
      this.buttonText = 'Logout';
      this.buttonAction = () => this.authService.logout(true);
    } else {
      // Otherwise, allow admin login.
      subtitleText.push(`If you are a TRG Administrator, please login below.`);

      this.showButton = true;
      this.buttonText = 'TRG Admin Login';
      this.buttonAction = () => this.authService.login();
    }

    this.subtitleText = subtitleText.join('\n');

    this.showDialog = true;
  }

  private handleError() {
    this.appUnavailable = true;

    this.titleText = 'Welcome!';
    this.subtitleText = 'You will be redirected to login shortly...';
  }

  buttonClick() {
    this.buttonAction();
  }


  ngOnDestroy() {
    this._checkAppStateSub?.unsubscribe();
  }

  logout() {
    this.store.dispatch(AppActions.logout());
  }
}
